import { useId } from "react";

type Kind = "gap" | "testing" | "evaluation" | "cases" | "policy";
type Panel = { title: string; text: string };
const figures: Record<Kind, { label: string; panels: Panel[]; caption: string }> = {
  gap: {
    label: "The questions behind the CDE Gap",
    panels: [
      { title: "What AI can do", text: "What happened in a test, with a particular model and set of instructions." },
      { title: "How it is used", text: "Who runs the system, what access it has, and whether it reaches anyone." },
      { title: "What changes", text: "Whether a person's beliefs or decisions change, and what caused that change." },
    ],
    caption: "These questions need separate evidence. Answering one does not settle the others.",
  },
  testing: {
    label: "The test and its limits",
    panels: [
      { title: "Inside the test", text: "Planning and sample content were recorded. Some requests were refused." },
      { title: "Outside the test", text: "No campaign was run. No real audience was contacted. No effect on voters was measured." },
    ],
    caption: "The test concerns assistance from AI, not a completed influence operation.",
  },
  evaluation: {
    label: "What a model test can leave out",
    panels: [
      { title: "The model", text: "What it says in response to a prompt." },
      { title: "The system", text: "What it can do with tools, memory, data and permission to act." },
      { title: "The person", text: "Whether someone understands the influence and can refuse or challenge it." },
    ],
    caption: "A result about the model alone does not answer every question about the system or its users.",
  },
  cases: {
    label: "Reading evidence from an election case",
    panels: [
      { title: "The attempt", text: "Evidence that a message was created or sent." },
      { title: "The audience", text: "Evidence that real people encountered it." },
      { title: "The response", text: "Evidence that it changed a belief, decision or institutional action." },
    ],
    caption: "Views are not changed votes. Missing evidence of an effect is not proof of no harm.",
  },
  policy: {
    label: "Questions to ask about a safeguard",
    panels: [
      { title: "Does it reach people?", text: "A warning or correction has to arrive where and when it can help." },
      { title: "Does it reduce the harm?", text: "The study needs to measure the problem the safeguard is meant to address." },
      { title: "Who gains control?", text: "Protecting the operator does not always protect the person affected." },
    ],
    caption: "These are questions for reviewing a safeguard, not a claim that it has been proved effective.",
  },
};

// Native, selectable text. No decorative map, bitmap, animation or implied score.
// Equal panels compare questions; they do not depict a proven causal sequence.
export function ResearchSchema({ kind = "gap" }: { kind?: Kind }) {
  const id = useId();
  const figure = figures[kind];
  return <figure className={`plain-schema plain-schema--${kind}`} data-native-schema={kind} aria-label={figure.label} aria-describedby={id}>
    <div className="plain-schema-panels" style={{ gridTemplateColumns: `repeat(${figure.panels.length}, minmax(0, 1fr))` }}>
      {figure.panels.map(panel => <div className="plain-schema-panel" key={panel.title}><h3>{panel.title}</h3><p>{panel.text}</p></div>)}
    </div>
    <figcaption id={id}>{figure.caption}</figcaption>
  </figure>;
}
